import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  ref,
  listAll,
  getMetadata,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { doc, getDoc } from "firebase/firestore";
import { db, storage } from "../utils/firebase";

interface FileItem {
  name: string;
  fullPath: string;
  size: number;
  url: string;
}

function cleanFilename(name: string): string {
  const ext = name.slice(name.lastIndexOf(".")).toLowerCase();
  const base = name
    .slice(0, name.lastIndexOf("."))
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return `${base}${ext}`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PremiumFiles: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [copyMenu, setCopyMenu] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<{ dev: string; prod: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const prefix = `content/${project}/premium/`;

  // Load deploy config for bucket names
  useEffect(() => {
    if (!project) return;
    getDoc(doc(db, "content", project, "_config", "deploy")).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setBuckets({
        dev: data.dev?.storage_bucket || "",
        prod: data.prod?.storage_bucket || "",
      });
    }).catch(() => {});
  }, [project]);

  const loadFiles = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    try {
      const listRef = ref(storage, prefix);
      const result = await listAll(listRef);
      const items: FileItem[] = await Promise.all(
        result.items.map(async (itemRef) => {
          const [meta, downloadUrl] = await Promise.all([
            getMetadata(itemRef),
            getDownloadURL(itemRef),
          ]);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: meta.size,
            url: downloadUrl,
          };
        })
      );
      items.sort((a, b) => a.name.localeCompare(b.name));
      setFiles(items);
    } catch (err) {
      console.error("[PremiumFiles] list error:", err);
    }
    setLoading(false);
  }, [project, prefix]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const filename = cleanFilename(file.name);
      const path = `${prefix}${filename}`;
      console.log("[PremiumFiles] uploading to:", path);
      await uploadBytes(ref(storage, path), file);
      console.log("[PremiumFiles] upload OK");
      await loadFiles();
    } catch (err: any) {
      const msg = err?.message || String(err);
      console.error("[PremiumFiles] upload error:", err);
      setError(msg);
    }
    setUploading(false);
  };

  const handleDelete = async (item: FileItem) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await deleteObject(ref(storage, item.fullPath));
      setFiles((prev) => prev.filter((f) => f.fullPath !== item.fullPath));
    } catch (err) {
      console.error("[PremiumFiles] delete error:", err);
    }
  };

  const handleCopy = async (item: FileItem, env: "dev" | "prod") => {
    setCopyMenu(null);
    const bucket = env === "prod" ? buckets?.prod : buckets?.dev;
    if (!bucket) return;
    const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(`content/premium/${item.name}`)}?alt=media`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(item.fullPath);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  return (
    <div className="cms__content">
      <div className="cms__content-header">
        <h2 className="cms__content-title">Premium Files</h2>
        <span className="cms__content-count">{files.length} files</span>
      </div>

      <input
        ref={fileInput}
        type="file"
        onChange={onFileChange}
        style={{ display: "none" }}
      />
      <div className="premium-files__upload-bar">
        <button
          className="cms__new-btn"
          disabled={uploading}
          onClick={() => fileInput.current?.click()}
        >
          {uploading ? "Uploading..." : "Upload File"}
        </button>
      </div>
      {error && <div className="premium-files__error">{error}</div>}

      {/* File list */}
      {loading ? (
        <div className="cms__loading">Loading files...</div>
      ) : files.length === 0 ? (
        <div className="cms__empty">No premium files yet.</div>
      ) : (
        <div className="cms__list">
          {files.map((item) => (
            <div key={item.fullPath} className="cms__item premium-files__row">
              <div className="cms__item-main">
                <div className="cms__item-title">{item.name}</div>
                <div className="cms__item-date">{formatSize(item.size)}</div>
              </div>
              <div className="premium-files__actions">
                <button
                  className="premium-files__btn"
                  onClick={() => window.open(item.url, "_blank")}
                  title="Open"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
                <a
                  className="premium-files__btn"
                  href={item.url}
                  download={item.name}
                  title="Download"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </a>
                <div className="premium-files__copy-wrap">
                  <button
                    className="premium-files__btn"
                    onClick={() => setCopyMenu(copyMenu === item.fullPath ? null : item.fullPath)}
                    title="Copy URL"
                  >
                    {copied === item.fullPath ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    )}
                  </button>
                  {copyMenu === item.fullPath && (
                    <div className="premium-files__copy-menu">
                      <button onClick={() => handleCopy(item, "dev")}>Stage</button>
                      <button className="premium-files__copy-menu--prod" onClick={() => handleCopy(item, "prod")}>Prod</button>
                    </div>
                  )}
                </div>
                <button
                  className="premium-files__btn premium-files__btn--danger"
                  onClick={() => handleDelete(item)}
                  title="Delete"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" /><path d="M14 11v6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PremiumFiles;
